import { mapListGroupingTypeToSearchSort } from '@epicuro-next/data/engagement/engagement-api';
import { ProductListGroupingType } from '@epicuro-next/data/engagement/engagement-models';
import { selectProductEntities } from '@epicuro-next/state/product-entities';
import { selectRouteParams } from '@epicuro-next/state/router-store';
import { selectUser } from '@epicuro-next/state/user-state';
import { isNonNullish, memoize } from '@epicuro-next/utilities';
import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';

import { LIST_ENTITIES_STATE_KEY } from './constants';
import { adapter } from './list-entities.adapter';
import { ListEntitiesState, ListEntity } from './model';
import { allProductsLoaded } from './utils';

export const selectOpenedListIdFromUrl = createSelector(
  selectRouteParams,
  (param) => {
    return param?.listId ?? param?.id;
  },
);
/**
 * Entities state from adapter
 */
export const selectListEntitiesState = createFeatureSelector<ListEntitiesState>(
  LIST_ENTITIES_STATE_KEY,
);

/**
 * Entities as Dictionary
 */
export const { selectEntities: selectListEntities } = adapter.getSelectors(
  selectListEntitiesState,
);

const selectOperableListId = createSelector(
  selectListEntitiesState,
  (state) => state.operableListId,
);

export const selectOpenedListId = createSelector(
  selectOpenedListIdFromUrl,
  selectOperableListId,
  (urlListId, operableListId) => {
    return urlListId ?? operableListId;
  },
);

/**
 * List entity with product references map to product entities state
 */
export const selectListEntityWithProducts = memoize((id: string) =>
  createSelector(
    selectListEntities,
    selectProductEntities,
    (
      listEntities: Dictionary<ListEntity>,
      productEntities: Dictionary<Product>,
    ) => getListEntityWithProducts(listEntities, productEntities, id),
  ),
);

/**
 * List entities with product references map to product entities state
 */
export const selectListEntitiesWithProducts = createSelector(
  selectListEntities,
  selectProductEntities,
  (
    listEntities: Dictionary<ListEntity>,
    productEntities: Dictionary<Product>,
    ids: string[],
  ) =>
    ids
      .map((id) => getListEntityWithProducts(listEntities, productEntities, id))
      .filter(isNonNullish),
);

export const selectListEntityFactory = memoize((id: string) =>
  createSelector(selectListEntities, (entities) => entities[id]),
);

export const selectOpenedListEntity = createSelector(
  selectOpenedListId,
  selectListEntities,
  (id, entities) => (id ? entities[id] : null),
);

export const selectListEntityIsUpdatingOrLoadingFactory = memoize(
  (id: string) =>
    createSelector(
      selectListEntityFactory(id),
      (entity) => !!entity && (entity.isUpdating || entity.loading),
    ),
);

export const selectListEntityIsLoadingFactory = memoize((id: string) =>
  createSelector(
    selectListEntityFactory(id),
    (entity) => !entity || entity.loading,
  ),
);

export const selectOpenedListFromListEntity = createSelector(
  selectOpenedListEntity,
  (entity) => entity?.list,
);

export const selectListEntitySearchSortType = (id?: string) => {
  return createSelector(
    id ? selectListEntityFactory(id) : selectOpenedListEntity,
    (entity) => {
      const userSelectedSortType = entity?.sortType;
      if (userSelectedSortType !== undefined) {
        return userSelectedSortType;
      }
      return mapListGroupingTypeToSearchSort(
        entity?.list?.groupingType as ProductListGroupingType,
      );
    },
  );
};

export const selectIsOwnerOfListEntity = memoize((id?: string) =>
  createSelector(
    selectUser,
    id ? selectListEntityFactory(id) : selectOpenedListEntity,
    (user, entity) => {
      return (
        user &&
        entity?.list &&
        user.externalId === entity.list.creation.user?.externalId
      );
    },
  ),
);

export const selectListEntity = createSelector(
  selectListEntities,
  (entities: Dictionary<ListEntity>, id: string) => entities[id],
);

export const selectListEntityProductsIsLoadedFactory = memoize((id: string) =>
  createSelector(
    selectListEntityFactory(id),
    (entity) => !!entity && allProductsLoaded(entity),
  ),
);

export const selectListEntityProductsIsLoaded = createSelector(
  selectListEntity,
  (entity) => !!entity && allProductsLoaded(entity),
);

export const selectListEntityProductsIsLoadingFactory = memoize((id: string) =>
  createSelector(
    selectListEntityFactory(id),
    (entity) => entity?.loadingProducts,
  ),
);

export const selectListEntityProductsIsLoading = createSelector(
  selectListEntity,
  (entity) => entity?.loadingProducts,
);

export const selectOpenedListPromotedToSpotlight = createSelector(
  selectOpenedListFromListEntity,
  (list) => !!list?.newsfeedId,
);

export const selectIsOpenedListDeleted = createSelector(
  selectOpenedListFromListEntity,
  (list) => Boolean(list?.isDeleted),
);

export function getListEntityWithProducts(
  listEntities: Dictionary<ListEntity>,
  productEntities: Dictionary<Product>,
  id: string,
) {
  const listEntity = listEntities[id];
  const list = listEntity?.list;

  if (!listEntity) return undefined;
  if (!list) return undefined;

  const productRefs = listEntity?.loadedProductIds ?? [];

  const products = productRefs
    .map((productId) => productEntities?.[productId])
    .filter(isNonNullish);

  return {
    ...listEntity,
    products,
  };
}
